import { Injectable } from '@angular/core';
import { Logger } from '@se/se.ui.html5';
import { ImgFileType } from './imgFileType';
import { DownloadWrapper } from './DownloadWrapper';
import { RasterImgWrapper } from './RasterImgWrapper';
import { IColorCode } from '../../models/IColorCode';
import { ImageType } from '../../models/ImageType.enum';
import { IPoint } from '../../models/IPoint';

@Injectable({
    providedIn: 'root',
})
export class ImgDownloaderService {
    private actualUrl = window.URL || window.webkitURL || (window as any);
    private imageLoadError = 'IMAGE_LOAD_ERROR ';
    private imgType: ImgFileType = 'png';
    private bottomLabelHeight = 20;

    constructor(private logger: Logger) {}

    // Metodi per la creazione/download di mappa e legenda
    public async downloadLinkCreator(
        svgs: SVGElement | SVGElement[],
        filename: string,
        widthAndHeight = 300,
        stage?: string,
        legend?: IColorCode[]
    ): Promise<DownloadWrapper> {
        return this.svgToRasterConvert(svgs, widthAndHeight, stage, legend).then((base64DataUrl: string) => {
            return new DownloadWrapper(base64DataUrl, filename);
        });
    }

    public async rasterImgCreator(
        svgs: SVGElement | SVGElement[],
        filename: string,
        widthAndHeight = 300,
        stage?: string,
        legend?: IColorCode[]
    ): Promise<RasterImgWrapper> {
        return this.svgToRasterConvert(svgs, widthAndHeight, stage, legend).then((base64DataUrl: string) => {
            return new RasterImgWrapper(base64DataUrl.split(',')[1], ImageType.IMAGE_ALL);
        });
    }

    private async svgToRasterConvert(
        svgs: SVGElement | SVGElement[],
        finalImgWidth = 1000,
        stageLabel?: string,
        legend?: IColorCode[]
    ): Promise<string> {
        try {
            if (!Array.isArray(svgs)) {
                svgs = [svgs];
            }

            // crea una griglia formata da {col,row} = {1,1}, {2,1}, {2,2}, {3,2}, {3,3}, {4,4} ..finchè la griglia non riesce a mantenere tutte le immagine
            const { columnNumber, rowNumber } = this.getMatrixFromNum(svgs.length);
            const legendSpanColNumber = 1;
            const singleMapWidthHeight = finalImgWidth / (columnNumber + legendSpanColNumber) ;
            const finalMapHeight = this.calcBaseCanvHeight(rowNumber, singleMapWidthHeight);
            const baseCanvas = this.addCanvasBackground(
                this.canvasCreator(finalImgWidth, finalMapHeight)
            );
            let y = 0, x = 0, svgsIndex = 0;

            for (let i = 0; i < rowNumber; i++) {
                x = 0;
                for (let j = 0; j < columnNumber; j++) {
                    const svgElement: SVGElement = svgs[svgsIndex].cloneNode(true) as SVGElement;
                    this.removeMapSelection(svgElement);
                    let canvasDataUrl = await this.svgToCanvasDataUrl(svgElement, singleMapWidthHeight)
                    if (stageLabel) {
                        canvasDataUrl = await this.addStageToCanvas(canvasDataUrl, stageLabel, singleMapWidthHeight, singleMapWidthHeight);
                    }
                    const loadedImg = await this.imgLoader(canvasDataUrl);
                    this.addImageToCanvas(baseCanvas, loadedImg, singleMapWidthHeight, singleMapWidthHeight, { x, y })
                    svgsIndex++;
                    if (svgsIndex === svgs.length) {
                        break
                    }
                    x += singleMapWidthHeight;
                }
                y += singleMapWidthHeight
            }

            // Generazione della Legenda
            const imgLegend: HTMLCanvasElement = this.createLegendImg(legend, singleMapWidthHeight);
            const legendImg: HTMLImageElement = await this.imgLoader(imgLegend.toDataURL());
            this.addImageToCanvas(baseCanvas, legendImg, singleMapWidthHeight, singleMapWidthHeight, { x: finalImgWidth - singleMapWidthHeight, y: 0 });

            const dataUrl = this.getDataUrlFromCanvas(baseCanvas);
            return dataUrl;
        } catch (err) {
            this.handleError(err);
            return null;
        }
    }

    // Metodi per la creazione/download della sola mappa

    public async viewsDownloadCreator(
        svgs: SVGElement | SVGElement[],
        filename: string,
        widthAndHeight = 300,
        stage?: string
    ): Promise<DownloadWrapper> {
        return this.viewToRasterConvert(svgs, widthAndHeight, stage).then((base64DataUrl: string) => {
            return new DownloadWrapper(base64DataUrl, filename);
        });

    }

    public async viewsImgCreator(
        svgs: SVGElement | SVGElement[],
        filename: string,
        widthAndHeight = 1000,
        bottomLabel?: string,
        guid?: string
    ): Promise<RasterImgWrapper> {
        return this.viewToRasterConvert(svgs, widthAndHeight, bottomLabel).then((base64DataUrl: string) => {

            return new RasterImgWrapper(base64DataUrl.split(',')[1], ImageType.IMAGE_MAP,guid);
        });
    }



    private async viewToRasterConvert(
        svgs: SVGElement | SVGElement[],
        finalImgWidth = 1000,
        bottomLabel?: string
    ): Promise<string> {
        try {
            if (!Array.isArray(svgs)) {
                svgs = [svgs];
            }

            // crea una griglia formata da {col,row} = {1,1}, {2,1}, {2,2}, {3,2}, {3,3}, {4,4} ..finchè la griglia non riesce a mantenere tutte le immagine
            const { columnNumber, rowNumber } = this.getMatrixFromNum(svgs.length);
            const singleMapWidthHeight = finalImgWidth / (columnNumber) ;
            const finalMapHeight = this.calcBaseCanvHeight(rowNumber, singleMapWidthHeight);
            const imgPaddingBottom = bottomLabel ? 10: 0; // correzione per disanziare eventaule scritta sul fondo
            const baseCanvas = this.addCanvasBackground(
                this.canvasCreator(finalImgWidth, finalMapHeight+imgPaddingBottom)
            );
            let y = 0, x = 0, svgsIndex = 0;

            for (let i = 0; i < rowNumber; i++) {
                x = 0;
                for (let j = 0; j < columnNumber; j++) {
                    const svgElement: SVGElement = svgs[svgsIndex].cloneNode(true) as SVGElement;
                    this.removeMapSelection(svgElement);
                    this.addStyleBeforRastering(svgElement);
                    let canvasDataUrl = await this.svgToCanvasDataUrl(svgElement, singleMapWidthHeight)
                    if (bottomLabel) {
                        canvasDataUrl = await this.addStageToCanvas(canvasDataUrl, bottomLabel, singleMapWidthHeight, singleMapWidthHeight);
                    }
                    const loadedImg = await this.imgLoader(canvasDataUrl);
                    this.addImageToCanvas(baseCanvas, loadedImg, singleMapWidthHeight, singleMapWidthHeight, { x, y })
                    svgsIndex++;
                    if (svgsIndex === svgs.length) {
                        break
                    }
                    x += singleMapWidthHeight;
                }
                y += singleMapWidthHeight
            }

            const dataUrl = this.getDataUrlFromCanvas(baseCanvas);
            return dataUrl;
        } catch (err) {
            this.handleError(err);
            return null;
        }
    }
    private addStyleBeforRastering(svgElement: SVGElement) {
        svgElement.style.fontFamily = 'Roboto, "Helvetica Neue", sans-serif';
    }

    // Metodi per la creazione/download della sola legenda

    public async legendDownloadCreator(
        legend: IColorCode[],
        filename: string,
        widthAndHeight = 400,
    ): Promise<DownloadWrapper> {
        return this.legendToRasterConvert(legend, widthAndHeight).then((base64DataUrl: string) => {
            return new DownloadWrapper(base64DataUrl,filename);
        })
    }

    public async legendImgCreator(
        legend: IColorCode[],
        filename: string,
        widthAndHeight = 400,
    ): Promise<RasterImgWrapper> {
        return this.legendToRasterConvert(legend, widthAndHeight).then((base64DataUrl: string) => {
            return new RasterImgWrapper(base64DataUrl.split(',')[1],ImageType.IMAGE_LEGEND,ImageType.IMAGE_LEGEND);
        });
    }

    private async legendToRasterConvert(
        legend: IColorCode[],
        finalImgWidth = 400,
    ): Promise<string> {
        try {
            
            // Generazione della Legenda
            const imgLegend: HTMLCanvasElement = this.createLegendImg(legend, finalImgWidth);
            const legendImg: HTMLImageElement = await this.imgLoader(imgLegend.toDataURL());
            const baseCanvas = this.addCanvasBackground(
                this.canvasCreator(imgLegend.width, finalImgWidth)
            );
            this.addImageToCanvas(baseCanvas, legendImg, imgLegend.width, finalImgWidth, { x: 0, y: 0 });
            const dataUrl = this.getDataUrlFromCanvas(baseCanvas);
            return dataUrl;
        } catch (err) {
            this.handleError(err);
            return null;
        }
    }


    // altri metodi accessori

    private removeMapSelection(svgElement: SVGElement): void{
        const pathsToDeselect: NodeListOf<SVGElement> = svgElement.querySelectorAll('[stroke="cyan"]');
        pathsToDeselect.forEach((path: SVGElement)=>{path.setAttribute('stroke','black')});
    }
    private createLegendImg(legend: IColorCode[], size: number): HTMLCanvasElement {
        const legendCanvas = this.canvasCreator(size, size);
        const rowHeight = size/Math.max(legend.length, 6);
        const circlePadding: number = rowHeight / 4;
        const circleRadius: number = (rowHeight - 2 * circlePadding) / 2;
        const fontSize: number = circleRadius * 1.5;


        legend.forEach((legendItem: IColorCode, index: number) => {
            const circolePos: IPoint = {
                x: circleRadius + 5,
                y: (index * rowHeight) + circleRadius + circlePadding
            };
            this.addCircle(legendCanvas, circleRadius, circolePos, legendItem.color);
            const label = legendItem.alphaCode + '. ' + legendItem.description;
            this.addTextToCanvas(legendCanvas, rowHeight - 10, (index * rowHeight) + (rowHeight / 2), label, fontSize, "start", "middle");


            // rowCanvas.toBlob(async (blob)=>{
            //    const imgBmp = await createImageBitmap(blob);
            //    this.addImageToCanvas(legendCanvas, imgBmp, imgBmp.width, imgBmp.height, {x: 0, y: index * rowHeight});
            // })

        })

        return legendCanvas;
    }

    private calcBaseCanvHeight(numOfRows: number, singleMapHeight: number) {
        return singleMapHeight * numOfRows;
    }

    private getMatrixFromNum(elements: number) {
        let columnNumber = 1;
        let rowNumber = 1;
        let i= 1;
        while ((rowNumber * columnNumber) < elements) {
            if (i % 2 === 0) {
                rowNumber++;
            } else {
                columnNumber++;
            }
            i++;
        }
        return {
            columnNumber,
            rowNumber
        };
    }

    private canvasCreator(width: number, height: number): HTMLCanvasElement {
        let canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        return canvas;
    }

    private addCanvasBackground(canvas: HTMLCanvasElement, fill = 'white'): HTMLCanvasElement {
        let context = canvas.getContext('2d');
        context.beginPath();
        context.fillStyle = fill;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.stroke();
        return canvas;
    }

    private async svgToCanvasDataUrl(svg: SVGElement, widthAndHeight: number): Promise<string> {
        const backgroundRef = this.checkForBackground(svg);
        if (backgroundRef) {
            const updatedSrc = await this.parseImageToSrc(
                backgroundRef.href.baseVal,
                widthAndHeight,
                widthAndHeight
            );
            backgroundRef.setAttribute('href', updatedSrc);
        }
        const src = this.actualUrl.createObjectURL(this.blobCreator(svg));
        return this.parseImageToSrc(src, widthAndHeight, widthAndHeight);
    }

    private checkForBackground(svg: SVGElement): SVGImageElement | undefined {
        return svg.getElementsByTagName('image')[0];
    }

    private blobCreator(svg: SVGElement): Blob {
        const data = new XMLSerializer().serializeToString(svg);
        return new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    }

    private async parseImageToSrc(href: string, width: number, height: number): Promise<string> {
        const loadedImg = await this.imgLoader(href);
        const canvasWithImage = this.addImageToCanvas(this.canvasCreator(width, height), loadedImg, width, height);
        return this.getDataUrlFromCanvas(canvasWithImage);
    }

    private async addStageToCanvas(canvasDataurl: string, stage: string, width: number, height: number) {
        this.bottomLabelHeight = height * 0.08;
        const newHeight = height + this.bottomLabelHeight;
        const previousMapImg = await this.imgLoader(canvasDataurl);
        const canvas = this.addCanvasBackground(this.canvasCreator(width, newHeight));
        this.addImageToCanvas(canvas, previousMapImg, width, newHeight)
        // return this.getDataUrlFromCanvas(this.addStageTextToCanvas(canvas, width, newHeight, stage));
        const fontSize = this.bottomLabelHeight;
        return this.getDataUrlFromCanvas(this.addTextToCanvas(canvas, width / 2, height + (this.bottomLabelHeight / 2), stage, fontSize, "center", "middle"));
    }

    private handleError(err: any): void {
        let text = 'link';
        if (err.type === this.imageLoadError) {
            text = 'image';
        }
        this.logger.error(`There was an error creating the ${text}`);
    }

    private imgLoader(src: string): Promise<HTMLImageElement> {
        return new Promise((res, rej) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.addEventListener('load', () => {
                res(img);
            });
            img.addEventListener('error', () => {
                rej(new ErrorEvent(this.imageLoadError));
            });
            img.src = src;
        });
    }

    private addImageToCanvas(
        canvas: HTMLCanvasElement,
        image: HTMLImageElement,
        width: number,
        height: number,
        position: IPoint = { x: 0, y: 0 }
    ): HTMLCanvasElement {
        let context = canvas.getContext('2d');
        const zoomCoefficient = Math.min(width / image.width, height / image.height);
        context.drawImage(image, position.x, position.y, image.width * zoomCoefficient, image.height * zoomCoefficient);
        return canvas;
    }

    private addStageTextToCanvas(canvas: HTMLCanvasElement, width: number, height: number, stage: string) {
        const fontSize = (width < height ? width : height) / 11;
        this.addTextToCanvas(canvas, 10, height - 10, `Stage: ${stage}`, fontSize, "center");
        return canvas;
    }


    private addTextToCanvas(canvas: HTMLCanvasElement, xPosition: number, yPosition: number, text: string, fontSize: number, hAlign?: CanvasTextAlign, vAlign?: CanvasTextBaseline) {
        const context = canvas.getContext('2d');
        context.font = fontSize + `px sans-serif`;
        if (vAlign) {
            context.textBaseline = vAlign
        };
        if (hAlign) {
            context.textAlign = hAlign
        };
        context.fillStyle = 'black';
        context.fillText(text, xPosition, yPosition);
        return canvas;
    }

    private addCircle(canvas: HTMLCanvasElement, radius: number, position: IPoint, color: string) {
        const context = canvas.getContext('2d');
        context.beginPath();
        context.strokeStyle = 'black';
        context.fillStyle = color;
        context.arc(position.x, position.y, radius, 0, Math.PI * 2, true);
        context.stroke();
        context.fill();
        context.closePath();
        return canvas;
    }

    private getDataUrlFromCanvas(canvas: HTMLCanvasElement): string {
        document.body.appendChild(canvas);
        const canvasDataUrl = this.canvasToDataUrl(canvas, this.imgType);
        document.body.removeChild(canvas);
        return canvasDataUrl;
    }

    private canvasToDataUrl(canvas: HTMLCanvasElement, imgFileType: ImgFileType): string {
        switch (imgFileType) {
            // case 'jpeg':
            //   return canvas.toDataURL('image/jpeg');
            // case 'webp':
            //   return canvas.toDataURL('image/webp');
            default:
                return canvas.toDataURL();
        }
    }

}
